import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { toLocalDateFormat } from '../../utils/functions';

const Container = styled.div`
  position: relative;
  border-radius: 0.75rem;
  padding: 0.5rem;
  background: white;
  display: flex;
  flex-direction: column;
  min-height: 0; // Permite que el contenedor se encoje
  flex: 1; // Ocupa todo el espacio disponible del padre
  overflow: hidden; // Previene scroll externo
`;

const CompactHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.5rem;
  flex-shrink: 0; // Evita que el header se encoja
  height: auto; // Altura natural del contenido
  
  .stats {
    display: flex;
    gap: .8rem;
    
    .stat-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      
      .label {
        color: #6c757d;
        font-size: 0.7rem;
        text-transform: uppercase;
        font-weight: 500;
        margin-bottom: 0.2rem;
      }
      
      .value {
        font-weight: 600;
        font-size: 0.95rem;
      }
      
      &.deuda .value {
        color: #dc3545;
        font-size: 1.05rem;
      }

      &.deuda-neta .value {
        color: #cfbf2a;
        font-size: 1.05rem;
      }
    }
  }
  
  .back-button {
    background: none;
    border: none;
    color: #0d6efd;
    font-size: 0.8rem;
    cursor: pointer;
    padding: 0.4rem 0.8rem;
    border-radius: 0.4rem;
    transition: all 0.2s;
    font-weight: 500;
    
    &:hover {
      background-color: #e7f1ff;
      transform: translateY(-1px);
    }
    
    &:active {
      background-color: #cce5ff;
      transform: translateY(0);
    }
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 0.75rem;
  position: relative;
  flex-shrink: 0; // Evita que la búsqueda se encoja
  height: auto; // Altura natural del contenido
  
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-size: 0.9rem;
    pointer-events: none;
  }
  
  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 2px solid #e9ecef;
    border-radius: 0.6rem;
    font-size: 0.95rem;
    outline: none;
    transition: all 0.2s;
    
    &:focus {
      border-color: #86b7fe;
      box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
    }
    
    &::placeholder {
      color: #adb5bd;
    }
  }
  
  .clear-button {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0.3rem;
    border-radius: 0.3rem;
    transition: all 0.2s;
    
    &:hover {
      color: #495057;
      background-color: #f8f9fa;
    }
  }
`;

const OrderList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden; // Previene scroll horizontal
  padding: 0 0.25rem 1.5rem 0.25rem; // padding-bottom para espacio extra
  min-height: 0; // Permite que el contenedor se encoja

  max-height: 600px; // Limita la altura máxima para evitar scroll excesivo en pantallas grandes
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
    
    &:hover {
      background: #a8a8a8;
    }
  }

  // SOLO en pantallas pequeñas: limitar altura máxima
  @media (max-width: 768px) {
    max-height: calc(100vh - 180px); // Ajusta 180px según el alto de tu header + search + paddings
  }
`;

const OrderCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 0.6rem;
  padding: 1rem;
  margin-bottom: 0.6rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    border-color: #86b7fe;
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  .main-info {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.5rem;
    
    .left {
      flex: 1;
      
      .order-id {
        font-weight: 600;
        color: #495057;
        font-size: 1.1rem;
        margin-bottom: 0.3rem;
      }
      
      .customer {
        color: #6c757d;
        font-size: 0.9rem;
        margin-bottom: 0.2rem;
      }
      
      .date-doc {
        display: flex;
        gap: 0.5rem;
        font-size: 0.85rem;
        color: #868e96;
        
        .doc {
          background: #f8f9fa;
          padding: 0.2rem 0.4rem;
          border-radius: 0.3rem;
        }
      }
    }
    
    .amount {
      text-align: right;
      
      .label {
        font-size: 0.75rem;
        color: #6c757d;
        text-transform: uppercase;
        margin-bottom: 0.2rem;
      }
      
      .value {
        font-weight: 600;
        color: #dc3545;
        font-size: 1.2rem;
      }
    }
  }
  
  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .payments-count {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.85rem;
      color: #6c757d;
      
      .count {
        background: #e3f2fd;
        color: #1976d2;
        padding: 0.2rem 0.5rem;
        border-radius: 0.3rem;
        font-weight: 500;
      }
    }
    
    .action-hint {
      font-size: 0.8rem;
      color: #0d6efd;
      font-style: italic;
    }
  }
`;

const DetailPanel = styled.div`
  background: #f8f9fa;
  border-radius: 0.6rem;
  padding: 1rem;
  margin-top: 0.5rem;
  
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    
    .title {
      font-weight: 600;
      color: #495057;
      font-size: 1rem;
    }
    
    .close-button {
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      font-size: 1.1rem;
      padding: 0.2rem;
      border-radius: 0.3rem;
      
      &:hover {
        color: #495057;
        background-color: #e9ecef;
      }
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  
  button {
    flex: 1;
    padding: 0.6rem;
    border: none;
    border-radius: 0.4rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    
    &.primary {
      background-color: #0d6efd;
      color: white;
      
      &:hover {
        background-color: #0b5ed7;
      }
    }
    
    &.secondary {
      background-color: #6c757d;
      color: white;
      
      &:hover {
        background-color: #5c636a;
      }
    }
    
    &:active {
      transform: translateY(1px);
    }
  }
`;

interface Order {
  id: number;
  documentNumber?: string;
  saleDate: string;
  deliveryDate?: string | null;
  toPay: number;
  toPayAdjusted?: number;
  total?: number; // Nuevo campo: total de la venta
  customerName: string;
  paymentsCount?: number;
}

interface CleanCuentasPorCobrarListProps {
  orders: Order[];
  totalDebt: number;
  deudaNeta: number;
  onBack: () => void;
  onRequestPayment: (orderId: number) => void;
  onViewPayments: (orderId: number) => void;
}

export const CleanCuentasPorCobrarList: React.FC<CleanCuentasPorCobrarListProps> = ({
  orders,
  totalDebt,
  deudaNeta,
  onBack,
  onRequestPayment,
  onViewPayments
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Filtrar órdenes
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;
    
    const term = searchTerm.toLowerCase();
    return orders.filter(order => 
      order.id.toString().includes(term) ||
      order.customerName.toLowerCase().includes(term) ||
      (order.documentNumber && order.documentNumber.toLowerCase().includes(term))
    );
  }, [orders, searchTerm]);

  const clearSearch = () => setSearchTerm('');

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(selectedOrder?.id === order.id ? null : order);
  };

  const remainingBalance = (order: Order) => 
    Number(order.toPayAdjusted ?? order.toPay);

const formatCurrency = (amount: number) => {
  return `S/ ${amount.toFixed(2)}`;
};

  return (
    <Container>
      {/* Header compacto */}
      <CompactHeader>
        <div className="stats">
          <div className="stat-item">
            <span className="label">Ventas</span>
            <span className="value">{orders.length}</span>
          </div>
          <div className="stat-item deuda">
            <span className="label">Deuda</span>
            <span className="value">S/ {totalDebt.toFixed(2)}</span>
          </div>
          <div className="stat-item deuda-neta">
            <span className="label">Deuda Neta</span>
            <span className="value">S/ {deudaNeta.toFixed(2)}</span>
          </div>
        </div>
        <button className="back-button" onClick={onBack}>
          ← Menú
        </button>
      </CompactHeader>

      {/* Búsqueda */}
      <SearchContainer>
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Buscar por N° orden, cliente o documento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="clear-button" onClick={clearSearch}>
            ✕
          </button>
        )}
      </SearchContainer>

      {/* Lista de órdenes */}
      <OrderList>
        {filteredOrders.map((order) => (
          <OrderCard key={order.id} onClick={() => handleOrderClick(order)}>
            <div className="main-info">
              <div className="left">
                <div className="order-id">Orden N° {order.id}</div>
                <div className="customer">{order.customerName}</div>
                <div className="date-doc">
                  <span>{toLocalDateFormat(order.deliveryDate)}</span>
                  {order.documentNumber && (
                    <span className="doc">{order.documentNumber}</span>
                  )}
                </div>
              </div>
              <div className="amount">
                <div className="label">Saldo</div>
                <div className="value">{formatCurrency(remainingBalance(order))}</div>
              </div>
            </div>
            
            <div className="footer">
              <div className="payments-count">
                <span>Cobros:</span>
                <span className="count">{order.paymentsCount || 0}</span>
              </div>
              <div className="action-hint">
                {selectedOrder?.id === order.id ? 'Ocultar detalles ▲' : 'Ver detalles ▼'}
              </div>
            </div>

            {/* Panel de detalles desplegable */}
            {selectedOrder?.id === order.id && (
              <DetailPanel>
                <div className="detail-header">
                  <div className="title">Detalles de Orden #{order.id}</div>
                  <button 
                    className="close-button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOrder(null);
                    }}
                  >
                    ✕
                  </button>
                </div>
                
                <div style={{ fontSize: '0.9rem', color: '#495057', marginBottom: '0.75rem' }}>
                  <div><strong>Cliente:</strong> {order.customerName}</div>
                  {order.documentNumber && (
                    <div><strong>Documento:</strong> {order.documentNumber}</div>
                  )}
                  <div><strong>Fecha de entrega:</strong> {toLocalDateFormat(order.deliveryDate)}</div>
                  {order.total && (
                    <div><strong>Total de venta:</strong> {formatCurrency(order.total)}</div>
                  )}
                  <div><strong>Saldo pendiente:</strong> {formatCurrency(remainingBalance(order))}</div>
                  <div><strong>Cobros realizados:</strong> {order.paymentsCount || 0}</div>
                </div>

                <ActionButtons>
                  <button 
                    className="secondary" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewPayments(order.id);
                    }}
                  >
                    📋 Ver Cobros
                  </button>
                  <button 
                    className="primary" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestPayment(order.id);
                    }}
                  >
                    💰 Registrar Cobro
                  </button>
                </ActionButtons>
              </DetailPanel>
            )}
          </OrderCard>
        ))}

        {/* Mensaje cuando no hay resultados */}
        {filteredOrders.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            color: '#6c757d',
            fontSize: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '0.6rem',
            border: '2px dashed #dee2e6'
          }}>
            {searchTerm 
              ? '🔍 No se encontraron ventas que coincidan con la búsqueda'
              : '📭 No hay ventas por cobrar'
            }
          </div>
        )}
      </OrderList>
    </Container>
  );
};